import { get, post } from "./client";

// staff-bff exposes every discovered service route under
// /api/v1/<service-short-name>/… (see discoveryRoutes.ts's default convention), so an
// address-book path registered as /api/v1/company-slug-drift is reachable here as
// /v1/address-book/company-slug-drift. Calling it without the service segment 404s.

/**
 * SOFTSUPP-532 — offices whose denormalised company identity has drifted from the parent
 * company. The three categories carry very different risk, which is why the tool keeps
 * them apart rather than offering one "fix everything" button:
 *
 *   company_slug / company_name — repair a denormalised copy. No URL changes.
 *   office_slug_prefix          — rewrites the office page's own address.
 */
export type DriftCategory = "company_slug" | "company_name" | "office_slug_prefix";

export type DriftRow = {
  officeId: string;
  companyId: string;
  companyName: string;
  category: DriftCategory;
  current: string | null;
  proposed: string;
};

export type DriftScanResponse = {
  rows: DriftRow[];
  summary: Record<DriftCategory, number>;
  /** The scan hit its row cap — the operator has NOT seen every drifted row. */
  truncated: boolean;
  requestId: string | null;
};

export type ApplyStatus = "applied" | "stale" | "not_found" | "noop" | "failed";

export type ApplyRowResult = {
  officeId: string;
  category: DriftCategory;
  status: ApplyStatus;
  detail?: string;
  /**
   * The value the office held BEFORE the write, captured server-side inside the same
   * transaction. The apply endpoint overwrites in place and keeps no history, so this is
   * what makes a run reversible — see rollback.ts.
   */
  previous?: string | null;
};

export type ApplyResponse = {
  results: ApplyRowResult[];
  summary: Record<ApplyStatus, number>;
  requestId: string | null;
};

async function scan(limit?: number): Promise<DriftScanResponse> {
  const query = limit ? `?limit=${limit}` : "";
  return get(`/v1/address-book/company-slug-drift${query}`);
}

/**
 * "repair" writes what the current company implies. "restore" writes the supplied value
 * verbatim — required for a rollback file, whose values deliberately no longer match the
 * company and which "repair" would therefore refuse as stale.
 */
export type ApplyMode = "repair" | "restore";

async function apply(
  rows: Array<Pick<DriftRow, "officeId" | "category" | "proposed">>,
  mode: ApplyMode = "repair",
): Promise<ApplyResponse> {
  return post("/v1/address-book/company-slug-drift/apply", { rows, mode });
}

export const companySlugDriftService = { scan, apply };
